/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useRef, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Stack,
    Flex,
    Text,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useGigya} from '../../context/gigya'

import CustomToast from '../account/customToast'
import {BRAND_KEY} from '../../brand'
import {cleanBrandsPreferences, setEntitlementsMarketing} from '../../utils/gigya'
import GMC from '../../utils/gigya/marketing'
// import {useLocation} from 'react-router'
// import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
// import {Helmet} from 'react-helmet'

const MarketingPreferences = () => {
    const styles = useMultiStyleConfig('MarketingPreferences')
    const headingRef = useRef()
    const [customToast, setCustomToast] = useState(null)
    const {data: customer} = useCurrentCustomer()
    // const location = useLocation()
    // const {customParams} = getConfig()
    // const currentPath = 'https://' + customParams.currentHost + location.pathname
    // const webPageSchemaMarkup = {
    //     '@context': 'https://schema.org/',
    //     '@type': 'WebPage',
    //     name: 'Account Marketing Preferences',
    //     description: 'Manage your marketing preferences',
    //     url: currentPath,
    // }

    const {isRegistered} = customer
    const {gigyaObject, gigyaLocale} = useGigya()
    const intl = useIntl()

    useEffect(() => {
        window.DLG = {
            ...(window.DLG || {}),
            EVE: {
                emit: (e) => {
                    if (e === 'GIGYA.PROFILE.INFO.BACK') {
                        window.scrollTo({
                            top: 0,
                            behavior: 'smooth',
                        })
                    }
                },
            },
            gigya: {brand: BRAND_KEY},
        }
    }, [])

    useEffect(() => {
        if (!gigyaObject || !isRegistered) return

        GMC.init()

        gigyaObject?.accounts.showScreenSet({
            containerID: 'marketing-preferences',
            startScreen: 'gigya-marketing-preferences-screen',
            screenSet: 'Default-ProfileUpdate',
            deviceType: 'desktop',
            lang: gigyaLocale,
            onBeforeScreenLoad: () => {},
            onAfterScreenLoad: (e) => GMC.onAfterScreenLoad(e),
            onSubmit: (e) => setEntitlementsMarketing(e),
            onAfterSubmit: ({sourceContainerID, response}) => {
                if (response.status === 'OK') {
                    cleanBrandsPreferences(sourceContainerID)
                    setCustomToast({
                        message: intl.formatMessage({
                            defaultMessage: 'Profile updated successfully',
                            id: 'account.marketing.profile_updated',
                        }),
                        status: 'success',
                    })
                } else {
                    setCustomToast({
                        message: response.error?.message || response?.errorMessage,
                        status: 'error',
                    })
                }
            },
            onBeforeValidation: () => {},
            onError: () => {},
            onFieldChanged: () => {},
        })
    }, [gigyaObject])

    return (
        <Stack data-testid="marketing-preferences-page" {...styles.accountDetailContainer}>
            {/* <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
            </Helmet> */}
            <Text as="h1" tabIndex="0" ref={headingRef} {...styles.accountTitle}>
                <FormattedMessage
                    defaultMessage="Marketing Preferences"
                    id="account.heading.marketing_preferences"
                />
            </Text>
            {customToast && (
                <CustomToast
                    message={customToast.message}
                    status={customToast.status}
                    onClose={() => setCustomToast(null)}
                    duration={5000}
                />
            )}
            <Stack {...styles.accountDetailContent}>
                <Flex id="marketing-preferences" __css={styles}></Flex>
            </Stack>
        </Stack>
    )
}

MarketingPreferences.getTemplateName = () => 'marketing-preferences'

export default MarketingPreferences
